import {
    EventName,
    HalfEvent,
    MatchEvent,
    SuspensionEvent,
} from "refsix-js-models";

// REFSIX-647: a suspended match (e.g. lightning) holds the clock. Event
// timestamps stay wall clock, but the suspended time is excluded from match
// time on every clock type: the watch records minuteOfPlay as if the suspension
// never happened, and anything that derives a duration or a minute from
// timestamps has to subtract it with these helpers. This is the one rule,
// written once here for the phone and the JS watch, and duplicated in
// MinuteOfPlayString.swift and MinuteOfPlayString.java. See docs/adr/0004.

export function suspensionsIn(
    events: MatchEvent[],
    half: number
): SuspensionEvent[] {
    return events.filter(
        (event) =>
            event.eventName === EventName.suspension && event.half === half
    ) as SuspensionEvent[];
}

// The suspension the match is currently in, if any. There is at most one.
export function openSuspension(
    events: MatchEvent[]
): SuspensionEvent | undefined {
    return events.find(
        (event) =>
            event.eventName === EventName.suspension &&
            (event as SuspensionEvent).endTime === undefined
    ) as SuspensionEvent | undefined;
}

export function isSuspended(events: MatchEvent[]): boolean {
    return openSuspension(events) !== undefined;
}

// Milliseconds the match was suspended for in the period up to `time`. A
// suspension still running at that moment counts only up to it, so a live
// clock evaluated during a suspension holds still. A corrupt one that ends
// before it starts counts as nothing, as on the watches.
export function suspensionMillisBefore(
    time: number,
    half: number,
    events: MatchEvent[]
): number {
    return suspensionsIn(events, half)
        .filter((suspension) => suspension.timestamp < time)
        .reduce(
            (total, suspension) =>
                total +
                Math.max(
                    0,
                    Math.min(suspension.endTime ?? time, time) -
                        suspension.timestamp
                ),
            0
        );
}

// Milliseconds the match was suspended for across a whole period. A corrupt
// suspension (starting after the period ended, or ending before it started)
// counts as nothing, as on the watches.
export function suspensionMillisInPeriod(
    period: HalfEvent,
    events: MatchEvent[]
): number {
    const periodEnd = period.endTime ?? Date.now();
    return suspensionsIn(events, period.index).reduce(
        (total, suspension) =>
            total +
            Math.max(
                0,
                Math.min(suspension.endTime ?? periodEnd, periodEnd) -
                    suspension.timestamp
            ),
        0
    );
}

// Match time played in the period at `time`: wall clock since the period
// started, minus the time it was suspended for.
export function playedMillisAt(
    time: number,
    period: HalfEvent,
    events: MatchEvent[]
): number {
    return (
        time -
        period.timestamp -
        suspensionMillisBefore(time, period.index, events)
    );
}

// The 1-based minute of play an event at `time` is recorded at: the watches'
// minuteInHalf, with the suspended time removed. The period's own minuteOfPlay
// is the base (0 on the watch, the accumulated match minute on the phone).
export function minuteInHalf(
    period: HalfEvent,
    time: number,
    events: MatchEvent[]
): number {
    return (
        Math.floor(playedMillisAt(time, period, events) / 60000) +
        period.minuteOfPlay +
        1
    );
}

// Inverse of playedMillisAt: the wall-clock time at which the period had been
// played for `playedMillis`, skipping over every completed suspension on the
// way. Used when the phone turns an edited minute back into a timestamp.
export function timestampForPlayedMillis(
    period: HalfEvent,
    playedMillis: number,
    events: MatchEvent[]
): number {
    let timestamp = period.timestamp + playedMillis;
    suspensionsIn(events, period.index)
        .filter((suspension) => suspension.endTime !== undefined)
        .sort((a, b) => a.timestamp - b.timestamp)
        .forEach((suspension) => {
            if (suspension.timestamp <= timestamp) {
                timestamp += suspension.endTime! - suspension.timestamp;
            }
        });
    return timestamp;
}

// "1:02:11" for over an hour, else "5:00". Shared by the phone and JS watch
// match logs so a suspension row reads the same on both.
export function formatSuspensionDuration(millis: number): string {
    const totalSeconds = Math.floor(millis / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (value: number) => String(value).padStart(2, "0");
    return hours > 0
        ? `${hours}:${pad(minutes)}:${pad(seconds)}`
        : `${minutes}:${pad(seconds)}`;
}
